import { useState } from "react";
import ExcelJS from "exceljs";
import {
    VIEW_ATTENDANCE_INNER_COLUMNS,
    VIEW_ATTENDANCE_OUTER_COLUMNS,
    getPunchHistoryForRow,
    getViewAttendanceExportDateTime,
    loadAllPunchHistory,
    mapPunchRowsForExport,
    mapViewAttendanceOuterRowsForExport,
} from "./attendanceViewExportHelpers";

const OUTER_HEADER_FILL = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "003366" },
};

const INNER_HEADER_FILL = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "DBEAFE" },
};

const centerCell = (cell) => {
    cell.alignment = { horizontal: "center", vertical: "middle" };
};

const styleHeaderRow = (row, fill, columnCount) => {
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
        centerCell(cell);
        if (colNumber <= columnCount) {
            cell.fill = fill;
        }
    });
};

export const ExportViewAttendanceExcel = ({ attendanceData = [] }) => {
    const [isExporting, setIsExporting] = useState(false);

    const exportToExcel = async () => {
        setIsExporting(true);

        try {
            const punchHistoryByEmployeeId = await loadAllPunchHistory(attendanceData);
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet("View Attendance");
            const boldFont = { bold: true };
            const outerColumnCount = VIEW_ATTENDANCE_OUTER_COLUMNS.length;
            const innerColumnCount = VIEW_ATTENDANCE_INNER_COLUMNS.length;

            worksheet.addRow(["", getViewAttendanceExportDateTime(), ""]);
            worksheet.mergeCells(1, 1, 1, outerColumnCount);
            worksheet.getCell(1, 1).font = boldFont;
            worksheet.getCell(1, 1).alignment = { horizontal: "center" };

            worksheet.addRow(["View Attendance Report"]);
            worksheet.mergeCells(2, 1, 2, outerColumnCount);
            worksheet.getCell(2, 1).font = boldFont;
            worksheet.getCell(2, 1).alignment = { horizontal: "center" };

            const outerHeaderRow = worksheet.addRow(VIEW_ATTENDANCE_OUTER_COLUMNS);
            styleHeaderRow(outerHeaderRow, OUTER_HEADER_FILL, outerColumnCount);

            mapViewAttendanceOuterRowsForExport(attendanceData).forEach(
                (outerRow, index) => {
                    const row = attendanceData[index];
                    const punches = getPunchHistoryForRow(row, punchHistoryByEmployeeId);

                    worksheet.addRow(outerRow).eachCell({ includeEmpty: true }, centerCell);

                    if (!punches.length) return;

                    const historyTitleRow = worksheet.addRow([
                        "",
                        `Punch History - ${row.employeeCode ?? row.empCode ?? "N/A"}`,
                    ]);
                    worksheet.mergeCells(
                        historyTitleRow.number,
                        2,
                        historyTitleRow.number,
                        1 + innerColumnCount
                    );
                    const titleCell = historyTitleRow.getCell(2);
                    titleCell.font = boldFont;
                    titleCell.fill = INNER_HEADER_FILL;

                    const innerHeaderRow = worksheet.addRow([
                        "",
                        ...VIEW_ATTENDANCE_INNER_COLUMNS,
                    ]);
                    styleHeaderRow(
                        innerHeaderRow,
                        INNER_HEADER_FILL,
                        innerColumnCount + 1
                    );

                    mapPunchRowsForExport(punches).forEach((innerRow) => {
                        worksheet
                            .addRow(["", ...innerRow])
                            .eachCell({ includeEmpty: true }, centerCell);
                    });

                    worksheet.addRow([]);
                }
            );

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `ViewAttendanceReport_${new Date().toISOString().split("T")[0]}.xlsx`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error generating Excel file:", error);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <button
            type="button"
            onClick={exportToExcel}
            className="button-container ms-0"
            disabled={isExporting || attendanceData.length === 0}
        >
            <i className="fa-solid fa-file-excel me-1 icon-container"></i>
            Excel
        </button>
    );
};
